import Utils from 'files/Utils.js';

const Appearance = {
    colors: {
        // Accent — used only for links and primary CTAs, never as a surface
        primary: () => '#0071E3',
        secondary: () => '#0077ED',
        tertiary: () => '#003D82',
        // Near-black text / dark surfaces
        dark: () => '#1D1D1F',
        darkGrey: '#1D1D1F',
        grey: '#6E6E73',
        lightGrey: '#86868B',
        lighterGrey: '#E8E8ED',
        // Grounds
        background: () => '#FFFFFF',
        white: '#FFFFFF',
        offWhite: '#F5F5F7',        // the Apple section grey + product image stage
        text: () => '#1D1D1F',
        subText: () => '#6E6E73',
        border: () => '#D2D2D7',
        divider: () => 'rgba(0,0,0,0.08)',
        success: '#2FA84F',
        error: '#E30000',
        warning: '#F56300',
        transparent: 'rgba(0,0,0,0)',
    },

    fonts: {
        // System stack → real San Francisco on Apple devices; clean sans elsewhere.
        // No serif: the display face and body face are one considered sans.
        heading: "-apple-system, system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        body: "-apple-system, system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    },

    textStyles: {
        heroHeading: (props) => ({
            fontFamily: Appearance.fonts.heading,
            fontWeight: 600,
            fontSize: Utils.isMobile() ? 44 : 76,
            lineHeight: 1.05,
            color: Appearance.colors.text(),
            letterSpacing: '-0.03em',
            ...props
        }),
        sectionHeading: (props) => ({
            fontFamily: Appearance.fonts.heading,
            fontWeight: 600,
            fontSize: Utils.isMobile() ? 30 : 44,
            lineHeight: 1.08,
            color: Appearance.colors.text(),
            letterSpacing: '-0.022em',
            textAlign: 'center',
            ...props
        }),
        sectionSubheading: (props) => ({
            fontFamily: Appearance.fonts.body,
            fontWeight: 400,
            fontSize: Utils.isMobile() ? 14 : 16,
            lineHeight: 1.6,
            color: Appearance.colors.subText(),
            textAlign: 'center',
            maxWidth: 725,
            ...props
        }),
        heading: (props) => ({
            fontFamily: Appearance.fonts.heading,
            fontWeight: 600,
            fontSize: Utils.isMobile() ? 24 : 32,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            color: Appearance.colors.text(),
            ...props
        }),
        subHeading: (props) => ({
            fontFamily: Appearance.fonts.body,
            fontWeight: 600,
            fontSize: Utils.isMobile() ? 18 : 22,
            lineHeight: 1.25,
            letterSpacing: '-0.015em',
            color: Appearance.colors.text(),
            ...props
        }),
        body: (props) => ({
            fontFamily: Appearance.fonts.body,
            fontWeight: 400,
            fontSize: Utils.isMobile() ? 14 : 16,
            lineHeight: 1.6,
            color: Appearance.colors.subText(),
            ...props
        }),
        caption: (props) => ({
            fontFamily: Appearance.fonts.body,
            fontWeight: 500,
            fontSize: 13,
            lineHeight: 1.4,
            color: Appearance.colors.lightGrey,
            ...props
        }),
        label: (props) => ({
            fontFamily: Appearance.fonts.body,
            fontWeight: 600,
            fontSize: 12,
            lineHeight: 1.3,
            color: Appearance.colors.primary(),
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            ...props
        }),
        navLink: (props) => ({
            fontFamily: Appearance.fonts.body,
            fontWeight: 500,
            fontSize: 15,
            color: Appearance.colors.text(),
            textDecoration: 'none',
            cursor: 'pointer',
            ...props
        }),
        price: (props) => ({
            fontFamily: Appearance.fonts.body,
            fontWeight: 700,
            fontSize: 18,
            color: Appearance.colors.text(),
            ...props
        }),
        stat: (props) => ({
            fontFamily: Appearance.fonts.heading,
            fontWeight: 600,
            fontSize: Utils.isMobile() ? 40 : 56,
            lineHeight: 1.0,
            letterSpacing: '-0.03em',
            fontVariantNumeric: 'tabular-nums',
            color: Appearance.colors.text(),
            ...props
        }),
    },

    styles: {
        card: () => ({
            backgroundColor: Appearance.colors.white,
            borderRadius: 16,
            overflow: 'hidden',
            border: `1px solid ${Appearance.colors.border()}`,
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        }),
        section: () => ({
            width: '100%',
            maxWidth: 1200,
            margin: '0 auto',
            padding: Utils.isMobile() ? '40px 20px' : '80px 40px',
        }),
        button: {
            primary: () => ({
                backgroundColor: Appearance.colors.primary(),
                color: Appearance.colors.white,
                border: 'none',
                borderRadius: 30,
                padding: '14px 32px',
                fontFamily: Appearance.fonts.body,
                fontWeight: 600,
                fontSize: 15,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
            }),
            secondary: () => ({
                backgroundColor: Appearance.colors.dark(),
                color: Appearance.colors.white,
                border: 'none',
                borderRadius: 30,
                padding: '14px 32px',
                fontFamily: Appearance.fonts.body,
                fontWeight: 600,
                fontSize: 15,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
            }),
            outline: () => ({
                backgroundColor: 'transparent',
                color: Appearance.colors.text(),
                border: `2px solid ${Appearance.colors.text()}`,
                borderRadius: 30,
                padding: '12px 30px',
                fontFamily: Appearance.fonts.body,
                fontWeight: 600,
                fontSize: 15,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
            }),
        },
        input: () => ({
            fontFamily: Appearance.fonts.body,
            fontSize: 15,
            padding: '14px 20px',
            borderRadius: 30,
            border: `1px solid ${Appearance.colors.border()}`,
            backgroundColor: Appearance.colors.white,
            color: Appearance.colors.text(),
            outline: 'none',
            transition: 'border-color 0.3s ease',
            width: '100%',
        }),
    },

    // Shared section rhythm — every section uses these so widths, padding, and
    // vertical spacing line up instead of each section choosing its own.
    layout: {
        sectionPadding: () => (Utils.isMobile() ? '64px 20px' : '96px 40px'),
        maxWidth: 1400,        // standard content width (grids, media, 2-col)
        readableWidth: 820,    // long-form text / accordions / single column

        // Fixed-header geometry. The header is pinned to headerHeight() so it cannot resize
        // with its contents — the cart and Shop buttons only render for online-sales visitors,
        // and without a fixed height the bar collapsed to the height of the logo until the
        // serviceability check resolved. Anything drawn beneath the header measures from the
        // same numbers, so they must be changed together and are therefore defined together.
        // These are border-box values (main.css sets it globally): headerHeight() includes
        // the vertical half of headerPadding().
        headerHeight: () => (Utils.isMobile() ? 72 : 76),
        headerPadding: () => (Utils.isMobile() ? '16px 20px' : '18px 40px'),

        // Limited-time offer strip drawn above the header row, inside the fixed header.
        // While it shows, LimitedOfferBanner publishes its height as --offer-banner-height
        // (0px otherwise), and everything positioned below the header adds that variable.
        offerBannerHeight: () => (Utils.isMobile() ? 34 : 36),

        // Top offset for content that starts below the fixed header, with a little extra
        // breathing room on mobile where the bar sits tighter against the content.
        headerOffset: () => `calc(${Appearance.layout.headerHeight() + (Utils.isMobile() ? 12 : 0)}px + var(--offer-banner-height, 0px))`,
    },

    boxShadow: {
        card: '0 4px 24px rgba(0,0,0,0.05)',
        cardHover: '0 18px 48px rgba(0,0,0,0.10)',
        button: '0 6px 18px rgba(0,113,227,0.22)',
        nav: '0 1px 0 rgba(0,0,0,0.05)',
    }
};

export default Appearance;
